import BusinessIcon from '@mui/icons-material/Business'
import DashboardIcon from '@mui/icons-material/Dashboard'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import GroupsIcon from '@mui/icons-material/Groups'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import MapIcon from '@mui/icons-material/Map'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import PeopleIcon from '@mui/icons-material/People'
import SchoolIcon from '@mui/icons-material/School'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { haveRole } from '@/lib/admin/getIsUserBackOffice'
import { routeBackOfficeAdminToolsCommon } from '@/lib/routes/backoffice/common/routeBackOfficeAdminToolsCommon'
import { MENU_GROUPS } from '@/lib/routes/menuGroups'
import { CONSTANTS } from '@/types/enum/constants'
import { CountryEnum } from '@/types/enum/enumUser'

/**
 * « Pilotage des pharmacies » au menu : France seulement (plus fin que le module Group : les
 * controles portent sur le SIRET, l'INSEE, la boutique, tous francais), super admin et service
 * Qualite des donnees — a qui s'adressent les tuiles.
 * Test du service ecrit en ligne : importer lib/admin/adminServices ajouterait un module a
 * l'arbre du shell (lint:shell-budget).
 */
const isPharmacyPilotageInMenu = (dataMyProfile: any): boolean =>
  !!dataMyProfile?.countries?.some((country: CountryEnum) => [CountryEnum.FRANCE].includes(country)) &&
  (haveRole(dataMyProfile?.loggedUserRoles, [CONSTANTS.ROLE_SUPER_ADMIN]) ||
    (dataMyProfile?.adminServiceCodes ?? []).includes('data_quality'))

export const routeBackOfficeAllEntityAdminCommon = [
  {
    route: 'Dashboard',
    text: 'Tableau de bord',
    icon: DashboardIcon,
    href: '/backoffice/group/dashboard-admin',
    menuHeader: true,
    home: true,
    // Home global du back-office : TOUJOURS de niveau 1. Sans ce flag, un admin mono-entite
    // (ex: admin School Belgique) ne recoit cette route commune que sous l'entite de son
    // perimetre et elle se retrouvait rangee dans « Mes formations ».
    menuFlat: true,
    position: 10,
  },
  {
    route: 'PharmaciesList',
    menuGroup: MENU_GROUPS.DIRECTORY,
    text: 'Liste des pharmacies',
    icon: LocalPharmacyIcon,
    href: '/backoffice/common/pharmacy',
    menuHeader: true,
    position: 20,
  },
  {
    // Pilotage des pharmacies (15/10/2026) : tuiles et onglets de controle des fiches ; les
    // rapprochements GERS et Store y sont des onglets. Espace Pharmedigroup (pas de `space`).
    // Groupe « Suivi & pilotage » depuis le 23/10/2026 (il etait dans « Annuaires »). Au menu
    // selon isPharmacyPilotageInMenu ; les autres admins y arrivent par les anciennes adresses
    // GERS et Store (redirigees), qui gardent leurs droits d'onglet.
    route: 'PharmacyPilotage',
    menuGroup: MENU_GROUPS.MONITORING,
    text: 'Pilotage des pharmacies',
    t: 'routes.backoffice.pharmacyPilotage',
    icon: FactCheckOutlinedIcon,
    href: '/backoffice/common/pharmacy/pilotage',
    menuHeader: true,
    position: 21,
    fnDisplay: ({ dataMyProfile }) => isPharmacyPilotageInMenu(dataMyProfile),
  },
  {
    // Ancienne adresse du rapprochement GERS : redirige vers l'onglet « GERS » du pilotage. Gardee
    // pour les liens (menu « Imports », rapport Eurodep, favoris).
    route: 'PharmacyGersReconciliation',
    text: 'Rapprochement GERS',
    icon: LocalPharmacyIcon,
    href: '/backoffice/common/pharmacy/gers-reconciliation',
    menuHeader: false,
    position: 21,
  },
  {
    // Rapprochement Store (SIRET de la fiche / du compte boutique) : ancienne adresse, redirige
    // vers l'onglet « Store » du pilotage (15/10/2026). HORS menu, ouvert depuis le
    // menu « Imports » de la liste des pharmacies. Super admin et service Qualite des donnees
    // (l'API le verifie : voter STORE_SIRET_RECONCILIATION) ; France seulement, plus fin que le
    // module Group : la boutique n'a de comptes clients qu'en France.
    route: 'PharmacyStoreSiretReconciliation',
    text: 'Rapprochement Store',
    icon: LocalPharmacyIcon,
    href: '/backoffice/common/pharmacy/store-siret-reconciliation',
    menuHeader: false,
    position: 23,
    fnDisplay: ({ dataMyProfile }) =>
      dataMyProfile?.countries?.some((country: CountryEnum) =>
        [CountryEnum.FRANCE].includes(country)
      ),
  },
  {
    // Guide « Fiche par SIREN : quel geste pour quel cas » (service Qualite des donnees) : HORS
    // menu, ouvert depuis la barre d'aide du rapprochement GERS. Memes roles que lui ; France
    // seulement, plus fin que le module Group : les gestes decrits n'existent qu'en France.
    route: 'PharmacySirenGuide',
    text: 'Fiche par SIREN : quel geste pour quel cas',
    icon: LocalPharmacyIcon,
    href: '/backoffice/common/pharmacy/gers-reconciliation/siren-guide',
    menuHeader: false,
    position: 22,
    fnDisplay: ({ dataMyProfile }) =>
      dataMyProfile?.countries?.some((country: CountryEnum) =>
        [CountryEnum.FRANCE].includes(country)
      ),
  },
  {
    route: 'MapsList',
    menuGroup: MENU_GROUPS.DIRECTORY,
    text: 'Cartes des pharmacies',
    icon: MapIcon,
    href: '/backoffice/common/maps',
    menuHeader: true,
    position: 20,
  },
  {
    route: 'CrmTaskList',
    menuGroup: MENU_GROUPS.CALL_CENTER,
    // Menu v2 : remonte dans « Actions a faire » quand des rappels/tickets sont en attente.
    actionable: true,
    text: 'Liste des tâches',
    icon: SupportAgentIcon,
    href: '/backoffice/group/crm-task',
    menuHeader: true,
    position: 410,
    fnBadge: ({ dataMyProfile }) =>
      !!dataMyProfile?.counterTaskEntityForMenu
        ? JSON.parse(dataMyProfile?.counterTaskEntityForMenu)?.numberAskContact
        : 0,
    fnBadges: ({ dataMyProfile }) => {
      if (!dataMyProfile?.counterTaskEntityForMenu) return []
      const counters = JSON.parse(dataMyProfile.counterTaskEntityForMenu)
      const badges = []
      if (counters?.numberAskContact) {
        badges.push({ count: counters.numberAskContact, color: 'error' as const })
      }
      if (counters?.numberSuggestionQuestion) {
        badges.push({ count: counters.numberSuggestionQuestion, color: '#0891b2' })
      }
      if (counters?.numberCareTicketsOpen) {
        badges.push({ count: counters.numberCareTicketsOpen, color: '#7B1FA2' })
      }
      return badges
    },
  },
  {
    route: 'CompanyList',
    menuGroup: MENU_GROUPS.DIRECTORY,
    text: 'Liste des entreprises',
    icon: BusinessIcon,
    href: '/backoffice/common/company/company-list',
    menuHeader: true,
    position: 150,
  },
  {
    route: 'HealthProfessionalCompaniesList',
    menuGroup: MENU_GROUPS.DIRECTORY,
    text: 'Entreprises médicales',
    icon: MedicalServicesIcon,
    href: '/backoffice/common/company/health-professional-list',
    menuHeader: true,
    position: 151,
    fnDisplay: ({ dataMyProfile }) =>
      dataMyProfile?.countries?.some((country: CountryEnum) =>
        [CountryEnum.FRANCE].includes(country)
      ),
  },
  {
    route: 'UserList',
    menuGroup: MENU_GROUPS.DIRECTORY,
    text: 'Liste des utilisateurs',
    icon: PeopleIcon,
    href: '/backoffice/common/user',
    menuHeader: true,
    position: 160,
  },
  {
    route: 'MyProfile',
    text: 'Mon profil',
    href: '/backoffice/common/my-profile',
    menuHeader: false,
  },
  {
    // Toutes les notifications de la cloche, en tableau : ouverte par « Voir toutes les
    // notifications » dans la cloche du header (France seulement, cf. NotificationsDisplay).
    route: 'MyNotifications',
    text: 'Mes notifications',
    href: '/backoffice/common/notifications',
    menuHeader: false,
  },
  {
    route: 'editCrmTask',
    text: 'Editer',
    href: '/backoffice/group/crm-task/edit-crm-task/[crmTaskId]',
  },
  {
    route: 'editUser',
    text: 'Editer un utilisateur',
    href: '/backoffice/common/user/edit-user/[userId]',
    menuHeader: false,
  },
  {
    route: 'editCompany',
    text: 'Edition établissement',
    href: '/backoffice/common/company/edit-company/[companyId]',
  },

  {
    route: 'AdminTeamsList',
    menuGroup: MENU_GROUPS.DIRECTORY,
    text: 'Liste des équipes officinales',
    icon: GroupsIcon,
    href: '/backoffice/group/user-team',
    menuHeader: true,
    position: 30,
    isMobile: true,
  },
  {
    route: 'TeamUserAdd',
    text: "Ajouter un membre de l'équipe",
    href: '/backoffice/group/user-team/[companyId]/add-user-team',
    menuHeader: false,
  },
  {
    route: 'GroupementOverviewAdmin',
    menuGroup: MENU_GROUPS.MONITORING,
    text: 'Suivi groupements',
    icon: HubOutlinedIcon,
    href: '/backoffice/group/groupement-overview',
    menuHeader: true,
    position: 315,
    // France uniquement (cf. .claude/rules/country-scope-front.md).
    fnDisplay: ({ dataMyProfile }) =>
      dataMyProfile?.countries?.some((country: CountryEnum) =>
        [CountryEnum.FRANCE].includes(country)
      ),
  },
  {
    route: 'Merlin',
    menuGroup: MENU_GROUPS.TOOLS,
    text: 'Merlin',
    icon: SmartToyIcon,
    href: '/backoffice/merlin',
    menuHeader: true,
    fnDisplay: ({ dataMyProfile }) =>
      dataMyProfile?.countries?.some((country: CountryEnum) =>
        [CountryEnum.FRANCE].includes(country)
      ) &&
      (haveRole(dataMyProfile.loggedUserRoles, [CONSTANTS.ROLE_SUPER_ADMIN]) ||
        dataMyProfile.id == 137828), //Sebastien LAP ,
  },
  // ATTESTATIONS & FORMATIONS (CDC attestation, P-14) : moteur documentaire du module
  // Procedures, mais entree TRANSVERSE — le vivier est TOUS les admins back-office
  // (arbitrage Mathieu 22/08), pas seulement l'equipe call. Groupe GESTION RH (2026-08-23).
  {
    // Raccourci RH vers l'onglet Suivi de la Base de connaissance (P-16) — role procedures only.
    // URL sous /backoffice/group (23/08) : sous /backoffice/call, le fil d'Ariane
    // heritait de « Outils Call Center », sans rapport avec la gestion RH. « Mes
    // attestations & formations » a disparu d'ici : c'est un ONGLET de Mon compte.
    route: 'callFormationsRh',
    menuGroup: MENU_GROUPS.RH,
    text: 'Attestations & formations',
    icon: SchoolIcon,
    href: '/backoffice/group/admin-formations',
    menuHeader: true,
    position: 501,
    fnDisplay: ({ dataMyProfile }) =>
      haveRole(dataMyProfile?.loggedUserRoles ?? [], [
        CONSTANTS.ROLE_SUPER_ADMIN,
        CONSTANTS.ROLE_PHARMEDICALL_PROCEDURE_ADMIN,
      ]),
  },
  ...routeBackOfficeAdminToolsCommon,
]
